import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiResponse } from '@/types/api';
import type { AuthResult } from '@/types/auth';
import { storage } from './storage';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Absolute-ish base for building <img>/<a> URLs (logos, document links) that the browser fetches
 * directly — these can't go through axios, so they must point at the API origin in production.
 * In dev this is "/api" (same-origin via the Vite proxy).
 */
export const apiBase = BASE_URL;

/** Error thrown for any non-successful API response, carrying the envelope fields. */
export class ApiError extends Error {
  status: number;
  errorCode: string | null;
  errors: string[] | null;
  /** Field-keyed validation messages (property name → messages), when the server provides them. */
  fieldErrors: Record<string, string[]> | null;
  traceId: string | null;

  constructor(
    message: string,
    status: number,
    errorCode: string | null = null,
    errors: string[] | null = null,
    traceId: string | null = null,
    fieldErrors: Record<string, string[]> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorCode = errorCode;
    this.errors = errors;
    this.fieldErrors = fieldErrors;
    this.traceId = traceId;
  }
}

// Raw axios instance (no envelope unwrap) — used for auth/refresh and binary calls.
export const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000, // surfaces as a friendly TIMEOUT rather than hanging
});

/**
 * Routes that wait on a real AI vendor call. Mistral/Claude take 15-20s for a short prompt and longer
 * for a package or itinerary, so the 30s default aborted them mid-flight: the browser cancelled, the
 * server's request token cancelled with it, and the half-finished handler surfaced a 500 AFTER the
 * vendor call had already completed and been billed. Only these routes get the longer ceiling - a
 * blanket raise would leave every ordinary failure spinning for three minutes.
 */
// Tour Management: building a tour, re-running it and generating a proposal price every hotel, segment and
// activity and may ask the AI for text — they take longer than an ordinary save.
const AI_ROUTE = /(^|\/)ai[-/]|\/generate(-missing)?$|\/test-connection$|^\/?tours\/?$|\/tours\/parse-request$|\/tours\/\d+\/(build|recalculate|proposals|validate|requirements|duplicate|content\/regenerate)$|\/trip-builder\/[^/]+$|\/trips\/[^/]+\/accept$/i;
const AI_TIMEOUT_MS = 180_000;

// Attach Authorization + X-Tenant to every request, and widen the timeout for AI generation routes.
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const headers = AxiosHeaders.from(config.headers);
  const token = storage.accessToken;
  const tenant = storage.tenantCode;
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  if (tenant) headers.set('X-Tenant', tenant);

  // Uploads: drop whatever Content-Type is on the request and let the browser write its own.
  //
  // This instance declares application/json by default, and a multipart body sent under that header is
  // unreadable to the server — [FromForm] binds nothing and the request comes back 400. Even an explicit
  // "multipart/form-data" is wrong on its own: the boundary is generated per request, so only the
  // browser can produce the complete header.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) headers.delete('Content-Type');

  config.headers = headers;
  if (AI_ROUTE.test(config.url ?? '')) config.timeout = AI_TIMEOUT_MS;
  return config;
});

// ---- Transparent refresh-token handling on 401 ----
let refreshPromise: Promise<string | null> | null = null;
let onUnauthorized: (() => void) | null = null;

/** Allows the auth layer to react to an unrecoverable 401 (e.g. force logout). */
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

/**
 * Exchanges the refresh token for a new pair.
 *
 * <p>
 * The server ROTATES refresh tokens — each one works once — and every tab of this browser shares
 * the same session. So refreshes are taken one at a time across tabs (a Web Lock where the browser
 * has them), a tab that finds the session already refreshed by another simply uses the new token,
 * and a failed refresh waits a beat for another tab's new pair before calling the session over.
 * </p>
 *
 * @param staleAccess the access token the failed request carried.
 */
async function refreshAccessToken(staleAccess: string | null): Promise<string | null> {
  const run = async (): Promise<string | null> => {
    // Another tab refreshed while this request was out: its token is already here.
    const current = storage.accessToken;
    if (current && staleAccess && current !== staleAccess) return current;

    const refreshToken = storage.refreshToken;
    if (!refreshToken) return null;
    try {
      const { data } = await axios.post<ApiResponse<AuthResult>>(
        `${BASE_URL}/auth/refresh-token`,
        { refreshToken, tenantCode: storage.tenantCode },
        { headers: { 'Content-Type': 'application/json', 'X-Tenant': storage.tenantCode ?? '' } },
      );
      if (data.success && data.data) {
        storage.setSession(data.data);
        return data.data.accessToken;
      }
    } catch { /* fall through to the re-check below */ }

    // Refused. If another tab rotated the token a moment ago, its new pair is on the way by
    // broadcast; only when nothing newer arrives is the session really over.
    await new Promise((resolve) => setTimeout(resolve, 400));
    const after = storage.refreshToken;
    return after && after !== refreshToken ? storage.accessToken : null;
  };

  const locks = (typeof navigator !== 'undefined'
    ? (navigator as Navigator & { locks?: { request: <T>(name: string, cb: () => Promise<T>) => Promise<T> } }).locks
    : undefined);
  return locks ? locks.request('st.refresh', run) : run();
}

/**
 * Renews the session from the refresh token alone — how start-up restores a session whose access
 * token is gone. Shares the one in-flight refresh with any request that is already renewing.
 */
export async function refreshSession(): Promise<boolean> {
  if (!storage.refreshToken) return false;
  refreshPromise = refreshPromise ?? refreshAccessToken(null);
  const token = await refreshPromise;
  refreshPromise = null;
  return !!token;
}

/**
 * Requests the session rules do not apply to: the sign-in endpoints themselves (a 401 from login is
 * a wrong password, not an expired session — renewing and signing every tab out over it would be
 * wrong twice) and the Platform Console, which has its own sign-in and its own refresh.
 */
const SESSIONLESS = /\/auth\/(login|refresh-token|logout|forgot-password|reset-password|verify-email|register)\b|\/platform\//i;

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !SESSIONLESS.test(original.url ?? '')) {
      if (!original._retry && storage.refreshToken) {
        // Once per request: the retry below carries _retry, so a request refused again never loops.
        original._retry = true;
        const sent = AxiosHeaders.from(original.headers).get('Authorization');
        const staleAccess = typeof sent === 'string' ? sent.replace(/^Bearer\s+/i, '') : null;
        // One refresh in flight per tab, shared by every request that hit the same 401.
        refreshPromise = refreshPromise ?? refreshAccessToken(staleAccess);
        const newToken = await refreshPromise;
        refreshPromise = null;
        if (newToken) {
          const headers = AxiosHeaders.from(original.headers);
          headers.set('Authorization', `Bearer ${newToken}`);
          original.headers = headers;
          return http(original);
        }
        storage.clearSession();
        onUnauthorized?.();
      } else if (storage.accessToken || storage.refreshToken) {
        // Refused again after a fresh token, or refused with nothing to renew it: the session is over.
        // Said so -- every tab goes to the login page -- rather than leaving the screen up with every
        // request quietly failing behind it.
        storage.clearSession();
        onUnauthorized?.();
      }
    }
    return Promise.reject(error);
  },
);

/** Converts an axios error into a normalized ApiError using the response envelope. */
export function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const body = error.response?.data as ApiResponse | undefined;
    if (body && typeof body === 'object' && 'success' in body) {
      return new ApiError(
        body.message || error.message || 'Request failed.',
        status,
        body.errorCode ?? null,
        body.errors ?? null,
        body.traceId ?? null,
        body.fieldErrors ?? null,
      );
    }
    // ASP.NET's own validation reply (ProblemDetails): { title, errors: { Field: [messages] } }. It comes back
    // before our handlers run, so it has no envelope; keep its field map so the screen can name the field.
    const problem = error.response?.data as { title?: string; errors?: Record<string, string[]>; traceId?: string } | undefined;
    if (problem && typeof problem === 'object' && problem.errors && typeof problem.errors === 'object' && !Array.isArray(problem.errors)) {
      const fieldErrors: Record<string, string[]> = {};
      for (const [key, msgs] of Object.entries(problem.errors)) {
        // "$.activity.durationMinutes" / "Activity.DurationMinutes" → the last part names the field.
        const field = key.replace(/^\$\.?/, '').split('.').filter(Boolean).pop() || key;
        fieldErrors[field] = Array.isArray(msgs) ? msgs : [String(msgs)];
      }
      return new ApiError('Please fix the highlighted information.', status, 'VALIDATION_ERROR',
        Object.values(fieldErrors).flat(), problem.traceId ?? null, fieldErrors);
    }
    // No envelope → a transport-level failure (no response). Distinguish timeout vs network.
    if (status === 0) {
      const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || /timeout/i.test(error.message ?? '');
      return isTimeout
        ? new ApiError('The request timed out.', 408, 'TIMEOUT')
        : new ApiError('Cannot reach the server.', 0, 'NETWORK_ERROR');
    }
    return new ApiError(error.message, status);
  }
  return new ApiError((error as Error)?.message ?? 'Unexpected error.', 0, 'NETWORK_ERROR');
}

/** Performs a request and unwraps `ApiResponse<T>.data`, throwing ApiError on failure. */
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await http.request<ApiResponse<T>>(config);
    const body = response.data;
    if (body && typeof body === 'object' && 'success' in body) {
      if (!body.success) {
        throw new ApiError(
          body.message || 'Request failed.',
          response.status,
          body.errorCode ?? null,
          body.errors ?? null,
          body.traceId ?? null,
          body.fieldErrors ?? null,
        );
      }
      return body.data as T;
    }
    // Non-enveloped payloads (rare) returned as-is.
    return body as unknown as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw toApiError(error);
  }
}

/**
 * Every verb takes an optional Axios config, not just POST.
 *
 * <p>
 * Added because a caller sometimes needs a one-off header — the onboarding setup token is sent that way,
 * since a token in a query string ends up in browser history and proxy logs. The parameter is optional,
 * so every existing call is unaffected.
 * </p>
 */
export const api = {
  get: <T>(url: string, params?: Record<string, unknown>, config?: AxiosRequestConfig) =>
    request<T>({ method: 'GET', url, params, ...config }),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ method: 'POST', url, data, ...config }),
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ method: 'PUT', url, data, ...config }),
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ method: 'PATCH', url, data, ...config }),
  del: <T>(url: string, config?: AxiosRequestConfig) =>
    request<T>({ method: 'DELETE', url, ...config }),
};
